import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";

// ========================================
// A2P Types and Interfaces
// ========================================

export type A2PStatus = 'not_started' | 'pending' | 'pending_review' | 'verified' | 'rejected' | 'expired' | 'not_submitted' | 'pending_payment';

/** Caller ID name (landlines) — one per business registration; sole props aren't eligible. */
export interface CallerIdState {
  eligible: boolean;
  canManage: boolean;
  registrationName: string | null;
  viaAgency: boolean;
  displayName: string | null;
  /** null | draft | pending-review | in-review | twilio-approved | twilio-rejected */
  status: string | null;
  suggestedName: string | null;
}
/** Per-registration fee for white-labels that charge (MRFG). Paid while the registration is in our review. */
export interface RegistrationFeeState {
  applies: boolean;
  due: boolean;
  status: 'not_applicable' | 'unpaid' | 'processing' | 'paid';
  amountCents: number;
  verificationId: string | null;
  paidAt: string | null;
  card: { brand: string; last4: string } | null;
}

export interface RegistrationFeePayResult {
  paid: boolean;
  processing?: boolean;
  checkoutUrl?: string;
  declined?: boolean;
}
export type VerificationType = 'independent'; // Force individual verification only
export type BrandType = 'sole_proprietor' | 'low_volume_standard' | 'standard';

export interface A2PStatusResponse {
  isVerified: boolean;
  status: A2PStatus;
  needsVerification: boolean;
  verificationId?: string;
  message: string;
  brandSid?: string;
  campaignSid?: string;
  messagingServiceSid?: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  // Brand type and status (for tracking OTP verification)
  brandType?: BrandType;
  /** Carrier the registration lives on. Telnyx sole props confirm a texted PIN in the app. */
  provider?: 'twilio' | 'telnyx';
  otpPending?: boolean;
  brandStatus?: 'pending' | 'approved' | 'rejected' | 'expired';
  campaignStatus?: 'pending' | 'approved' | 'rejected' | 'expired';
  // Campaign details (populated from verification record)
  businessName?: string;
  campaignName?: string;
  campaignDescription?: string;
  useCase?: string;
  // Phone numbers linked to campaign
  linkedPhoneNumbers?: number;
  // Message statistics
  messagesThisMonth?: number;
  messagesSent?: number;
  messagesDelivered?: number;
  messagesFailed?: number;
  // Campaign health (calculated from delivery rate)
  campaignHealth?: 'Excellent' | 'Good' | 'Needs Attention' | 'Poor';
  // Public landing page slug for agent subdomain
  publicSlug?: string;
}

export interface BusinessAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface A2PVerificationData {
  verificationType: VerificationType;
  brandType: BrandType;
  businessName: string;
  businessType: string;
  website?: string;
  businessAddress: BusinessAddress;
  businessPhone?: string;
  businessEmail: string;
  ein?: string;
  taxId?: string;
  // SSN for Sole Proprietor (required when brandType is sole_proprietor)
  ssn?: string;
  // Authorized rep (optional for sole_proprietor)
  authorizedRepFirstName?: string;
  authorizedRepLastName?: string;
  campaignName: string;
  campaignDescription: string;
  useCase: string;
  sampleMessages: string[];
  volumeEstimate: number;
}

export interface A2PVerificationResponse {
  verificationId: string;
  status: A2PStatus;
  brandSid?: string;
  campaignSid?: string;
  message: string;
}

export interface A2PHistoryItem {
  id: string;
  verificationType: VerificationType;
  brandType: BrandType;
  businessName: string;
  status: A2PStatus;
  brandSid?: string;
  campaignSid?: string;
  submittedAt: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
}

export interface A2PHistoryResponse {
  items: A2PHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ========================================
// A2P Service
// ========================================

class A2PService {
  /**
   * Check current A2P verification status
   */
  async checkStatus(): Promise<A2PStatusResponse> {
    try {
      // Add cache-busting timestamp to force fresh data fetch
      const cacheBuster = `_cb=${Date.now()}`;
      const a2pUrl = getApiUrl(endpoint.communication.a2p.status);
      const urlWithCacheBuster = a2pUrl.includes('?')
        ? `${a2pUrl}&${cacheBuster}`
        : `${a2pUrl}?${cacheBuster}`;

      // handleApiCall automatically unwraps the response.data for us
      // So the return value is ALREADY the A2PStatusResponse, not the wrapper
      const statusData = await handleApiCall<A2PStatusResponse>(async () => {
        return await api.get<{
          statusCode: number;
          message: string;
          data: A2PStatusResponse;
        }>({
          url: urlWithCacheBuster,
          auth: true // Ensure authentication header is included
        });
      });

      return statusData;
    } catch (error: any) {
      // Handle 401 errors gracefully - user might not be authenticated
      if (error?.response?.status === 401) {
        // Return a default status indicating verification is required
        return {
          isVerified: false,
          status: 'not_submitted',
          needsVerification: true,
          message: 'Authentication required for A2P verification status'
        };
      }

      // Handle 403 errors gracefully - user may not have permissions
      if (error?.response?.status === 403) {
        return {
          isVerified: false,
          status: 'not_submitted',
          needsVerification: true,
          message: 'Individual A2P verification required - no exceptions for any role or position'
        };
      }

      // Handle 500 errors gracefully - server might be having issues
      if (error?.response?.status === 500) {
        return {
          isVerified: false,
          status: 'not_submitted',
          needsVerification: true,
          message: 'A2P verification service is temporarily unavailable'
        };
      }

      // For all other errors, also return graceful response to avoid breaking UI
      return {
        isVerified: false,
        status: 'not_submitted',
        needsVerification: true,
        message: error.response?.data?.message || error.message || 'Individual A2P verification required - no exceptions for any role or position'
      };
    }
  }

  /**
   * Submit A2P verification request (for Sole Proprietor)
   */
  async submitVerification(data: A2PVerificationData): Promise<A2PVerificationResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      // So result will be A2PVerificationResponse directly
      const result = await handleApiCall<A2PVerificationResponse>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: A2PVerificationResponse;
        }>({
          url: getApiUrl(endpoint.communication.a2p.verify),
          body: data,
          auth: true // Ensure authentication header is included
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      if (result && result.verificationId) {
        return result;
      }

      throw new Error('Failed to submit A2P verification - no verification ID returned');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to submit A2P verification request'
      );
    }
  }

  /**
   * Check verification status for a specific verification ID
   */
  /** Telnyx sole proprietor: submit the 6-digit PIN that was texted to the business phone. */
  async verifyOtp(pin: string): Promise<{ message?: string }> {
    return handleApiCall<{ message?: string }>(async () =>
      api.post({ url: getApiUrl(endpoint.communication.a2p.otpVerify), body: { pin }, auth: true }),
    );
  }

  async resendOtp(): Promise<{ message?: string }> {
    return handleApiCall<{ message?: string }>(async () =>
      api.post({ url: getApiUrl(endpoint.communication.a2p.otpResend), body: {}, auth: true }),
    );
  }

  /** Caller ID name of the registration covering the user (landline caller ID). */
  async getCallerId(): Promise<CallerIdState> {
    return handleApiCall<CallerIdState>(async () =>
      api.get({ url: getApiUrl(endpoint.communication.a2p.callerId), auth: true }),
    );
  }

  async setCallerId(displayName: string): Promise<CallerIdState> {
    return handleApiCall<CallerIdState>(async () =>
      api.post({ url: getApiUrl(endpoint.communication.a2p.callerId), body: { displayName }, auth: true }),
    );
  }

  async getRegistrationFee(): Promise<RegistrationFeeState> {
    return handleApiCall<RegistrationFeeState>(async () =>
      api.get({ url: getApiUrl(endpoint.communication.a2p.registrationFee), auth: true }),
    );
  }

  /**
   * Charges the card on file; returns a Stripe Checkout URL when there isn't one or
   * it's declined. `checkout` skips the card on file ("Pay with a different card").
   */
  async payRegistrationFee(method?: 'checkout'): Promise<RegistrationFeePayResult> {
    return handleApiCall<RegistrationFeePayResult>(async () =>
      api.post({
        url: getApiUrl(endpoint.communication.a2p.registrationFeePay),
        body: method ? { method } : {},
        auth: true,
      }),
    );
  }

  async checkTwilioStatus(verificationId: string): Promise<A2PStatusResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<A2PStatusResponse>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: A2PStatusResponse;
        }>({
          url: getApiUrl(`${endpoint.communication.a2p.status}/${verificationId}`),
          auth: true // Ensure authentication header is included
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      return result;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to check verification status'
      );
    }
  }

  /**
   * Get A2P verification history
   */
  async getHistory(page: number = 1, limit: number = 10): Promise<A2PHistoryResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<A2PHistoryResponse>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: A2PHistoryResponse;
        }>({
          url: getApiUrl(`${endpoint.communication.a2p.history}?page=${page}&limit=${limit}`),
          auth: true // Ensure authentication header is included
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      return result;
    } catch (error: any) {
      // Handle all errors gracefully - return empty history to avoid breaking UI
      return {
        items: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0
      };
    }
  }

  /**
   * Helper to determine if user can send SMS.
   * IMPORTANT: status='verified' only means the BRAND is approved. The TCR campaign is a
   * separate review and can be rejected even when the brand is verified. SMS sent under a
   * rejected campaign drops to unregistered traffic, gets carrier-filtered, and tanks
   * deliverability. We must gate sending on the campaign as well.
   */
  async canSendSMS(): Promise<{ canSend: boolean; reason?: string; status?: A2PStatus; stage?: 'verified' | 'pending' | 'rejected' | 'required' }> {
    try {
      const status = await this.checkStatus();
      const anyStatus = status as any;
      // Normalize casing defensively — the backend should send our lowercase enum, but a stale
      // deploy or a raw Twilio value ('VERIFIED'/'APPROVED'/'FAILED') must never wrongly block a
      // fully-verified agent from texting. Bug seen 2026-07-15: campaignStatus came back as
      // 'VERIFIED' (uppercase) and the strict === 'approved' check blocked Ali Bazzi's SMS.
      const norm = (v?: string): string | undefined => {
        if (!v) return undefined;
        const u = v.toUpperCase();
        if (u === 'VERIFIED' || u === 'APPROVED') return 'approved';
        if (u === 'FAILED' || u === 'REJECTED') return 'rejected';
        if (u === 'EXPIRED') return 'expired';
        if (u === 'IN_PROGRESS' || u === 'PENDING' || u === 'IN_REVIEW') return 'pending';
        return v.toLowerCase();
      };
      const campaignStatus = norm(anyStatus.campaignStatus || anyStatus.campaign_status);
      const brandStatus = norm(anyStatus.brandStatus || anyStatus.brand_status);

      const s = status.status;
      // Campaign-level gates take priority — even if brand is verified, a rejected campaign
      // means messages won't deliver as registered A2P traffic.
      if (campaignStatus === 'rejected') {
        return {
          canSend: false, status: s, stage: 'rejected',
          reason: 'Your A2P SMS campaign was rejected by carrier review (TCR). SMS will not deliver as registered traffic until the campaign is fixed and resubmitted. Contact support.'
        };
      }

      if (campaignStatus === 'pending' || campaignStatus === 'in_progress') {
        return {
          canSend: false, status: s, stage: 'pending',
          reason: 'Your A2P SMS campaign is still in carrier review. Messages will be blocked until the campaign is approved (typically 1-4 business days).'
        };
      }

      // Allow when the campaign is approved. We accept either status==='verified' OR the
      // campaign being approved — a verified campaign IS the sole source of truth for delivery.
      if (campaignStatus === 'approved' && (status.isVerified || status.status === 'verified')) {
        return { canSend: true, status: s, stage: 'verified' };
      }

      if (status.status === 'pending' || status.status === 'pending_review') {
        return {
          canSend: false, status: s, stage: 'pending',
          reason: 'Your A2P verification is pending approval. SMS will unlock once it’s approved (typically 1-4 business days).'
        };
      }

      if (status.status === 'rejected' || brandStatus === 'rejected') {
        return {
          canSend: false, status: s, stage: 'rejected',
          reason: `A2P verification was rejected: ${status.rejectionReason || anyStatus.rejection_reason || 'Unknown reason'}`
        };
      }

      return {
        canSend: false, status: s, stage: 'required',
        reason: 'A2P verification is required to send SMS messages'
      };
    } catch (error) {
      return {
        canSend: false, stage: 'required',
        reason: 'Failed to check A2P verification status'
      };
    }
  }

  /**
   * Normalise an EIN to the XX-XXXXXXX the carrier expects.
   *
   * The field has no mask, so people type what's on the IRS letter — with the
   * hyphen, without it, with spaces, or with an en-dash pasted from a PDF. Only
   * the digits matter; everything else is presentation.
   */
  normalizeEIN(ein?: string | null): string {
    const digits = String(ein ?? '').replace(/\D/g, '');
    return digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : String(ein ?? '').trim();
  }

  /**
   * Validate EIN format.
   *
   * Normalises first. This used to test the raw input against /^\d{2}-\d{7}$/,
   * so an EIN typed without the hyphen failed at the FINAL submit — after the
   * whole wizard was filled in and the CP 575 uploaded. Sam Chehab lost a
   * completed application to it on 2026-09-22.
   */
  validateEIN(ein: string): boolean {
    return /^\d{2}-\d{7}$/.test(this.normalizeEIN(ein));
  }

  /**
   * Submit low-volume standard verification
   */
  async submitLowVolumeStandard(data: A2PVerificationData): Promise<A2PVerificationResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      // So result will be A2PVerificationResponse directly
      const result = await handleApiCall<A2PVerificationResponse>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: A2PVerificationResponse;
        }>({
          url: getApiUrl(endpoint.communication.a2p.verifyLowVolume),
          body: data,
          auth: true
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      if (result && result.verificationId) {
        return result;
      }

      throw new Error('Failed to submit Low-Volume Standard verification - no verification ID returned');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to submit Low-Volume Standard verification request'
      );
    }
  }

  /**
   * Retry rejected verification
   */
  async retryVerification(verificationId: string, data: A2PVerificationData): Promise<A2PVerificationResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<A2PVerificationResponse>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: A2PVerificationResponse;
        }>({
          url: getApiUrl(endpoint.communication.a2p.retry),
          body: { verificationId, ...data },
          auth: true
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      if (result && result.verificationId) {
        return result;
      }

      throw new Error('Failed to retry verification - no verification ID returned');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to retry verification request'
      );
    }
  }

  /**
   * Get pending phone numbers awaiting campaign assignment
   */
  async getPendingPhoneNumbers(): Promise<any[]> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<any[]>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: any[];
        }>({
          url: getApiUrl(endpoint.communication.a2p.pendingNumbers),
          auth: true
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      return result || [];
    } catch (error: any) {
      // Return empty array instead of throwing to avoid breaking UI
      return [];
    }
  }

  /**
   * Manually relink all phone numbers to the A2P campaign
   * Use this if auto-linking failed or to force a re-sync
   */
  async relinkPhoneNumbers(): Promise<{ success: boolean; linkedCount: number; message: string }> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<{ success: boolean; linkedCount: number; message?: string }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: { success: boolean; linkedCount: number };
        }>({
          url: getApiUrl(endpoint.communication.a2p.relinkPhoneNumbers),
          body: {},
          auth: true
        });
        return response;
      });

      return {
        success: result?.success ?? true,
        linkedCount: result?.linkedCount ?? 0,
        message: result?.message || 'Phone numbers relinked successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        linkedCount: 0,
        message: error.message || 'Failed to relink phone numbers'
      };
    }
  }

  /**
   * Upload CP 575 document for A2P verification
   * CP 575 is the IRS letter confirming your EIN number
   */
  async uploadCp575Document(file: File): Promise<{ uploadId: string; externalPath: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // handleApiCall automatically extracts the 'data' field from the response
      // So result will be { uploadId: string; externalPath: string } directly
      const result = await handleApiCall<{ uploadId: string; externalPath: string }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: { uploadId: string; externalPath: string };
        }>({
          url: getApiUrl(endpoint.communication.a2p.uploadCp575),
          body: formData,
          auth: true,
          options: {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      // Result is already the extracted data: { uploadId, externalPath }
      if (result && result.uploadId) {
        return result;
      }

      throw new Error('Failed to upload CP 575 document - no upload ID returned');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to upload CP 575 document'
      );
    }
  }

  /**
   * Submit low-volume standard verification with CP 575 document
   */
  async submitLowVolumeStandardWithCp575(data: A2PVerificationData & {
    cp575UploadId: string;
    einIsNew?: boolean;
    einAgeConfirmed?: boolean;
    /** Cover the caller's whole agency. The agency is resolved server-side
     *  from the authenticated context, never sent from here. */
    registerForAgency?: boolean;
  }): Promise<A2PVerificationResponse> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      // So result will be A2PVerificationResponse directly, not the wrapper
      const result = await handleApiCall<A2PVerificationResponse>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: A2PVerificationResponse;
        }>({
          url: getApiUrl(endpoint.communication.a2p.verifyLowVolume),
          body: data,
          auth: true
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      // Result is already the extracted data
      if (result && result.verificationId) {
        return result;
      }

      throw new Error('Failed to submit Low-Volume Standard verification - no verification ID returned');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Failed to submit Low-Volume Standard verification request'
      );
    }
  }

  /**
   * Check if payment is required based on subscription
   */
  async checkPaymentRequirement(): Promise<{ required: boolean; reason?: string }> {
    try {
      // handleApiCall automatically extracts the 'data' field from the response
      const result = await handleApiCall<{ required: boolean; reason?: string }>(async () => {
        const response = await api.get<{
          statusCode: number;
          message: string;
          data: { required: boolean; reason?: string };
        }>({
          url: getApiUrl(endpoint.communication.a2p.paymentCheck),
          auth: true
        });
        return response;
      });

      // handleApiCall throws on non-2xx status, so if we get here it succeeded
      return result;
    } catch (error: any) {
      // Default to requiring payment if check fails
      return { required: true, reason: 'Unable to verify subscription status' };
    }
  }
}

export const a2pService = new A2PService();
export default a2pService;